import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { DataTable } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";

export default function PacksTab() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || hasPermission("LANGUAGE.EDIT");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/v1/i18n/admin/packs", { params: { size: 300 } })).data.data.items); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put(`/v1/i18n/admin/packs/${active.id}`, { entries: active.entries });
      toast.success("Pack saved");
      setActive(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="packs-tab">
      <DataTable testid="packs-table" rows={items} loading={loading}
        columns={[
          { key: "lang", header: "Language", render: (p) => <span className="font-mono text-xs uppercase">{p.lang}</span> },
          { key: "namespace", header: "Namespace", render: (p) => <span className="font-medium text-foreground">{p.namespace}</span> },
          { key: "version", header: "Version", render: (p) => <span className="text-xs">{p.version || "—"}</span> },
          { key: "keys", header: "Keys", render: (p) => <span className="tabular-nums">{Object.keys(p.entries || {}).length}</span> },
          { key: "actions", header: "", render: (p) => canEdit ? <button onClick={() => setActive({ ...p })} className="text-xs text-teal-600 hover:underline" data-testid={`edit-pack-${p.lang}-${p.namespace}`}>Edit entries</button> : <span className="text-xs text-muted-foreground">view only</span> },
        ]} />

      {active && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="pack-editor-drawer">
          <button className="flex-1 bg-black/40" onClick={() => setActive(null)} aria-label="close" />
          <div className="w-full max-w-xl bg-card border-l border-border h-full overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">{active.namespace} · <span className="uppercase font-mono text-sm">{active.lang}</span></h3>
              <button onClick={() => setActive(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-2">
              {Object.entries(active.entries || {}).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[1fr_1.4fr] gap-2 items-center">
                  <span className="font-mono text-[11px] text-muted-foreground truncate" title={k}>{k}</span>
                  <input value={v} onChange={(e) => setActive({ ...active, entries: { ...active.entries, [k]: e.target.value } })}
                    className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm" data-testid={`pack-entry-${k}`} />
                </div>
              ))}
              {Object.keys(active.entries || {}).length === 0 && <div className="text-sm text-muted-foreground">No entries.</div>}
            </div>
            <div className="flex justify-end gap-2 mt-5 sticky bottom-0 bg-card py-2">
              <button onClick={() => setActive(null)} className="rounded-lg border border-border px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={save} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm" data-testid="save-pack-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
